import {
  GENERATE_AUTH_TOKEN,
  PAYMENT_SERVER_URL,
  STRIPE_BILLING_PORTAL_URL,
  STRIPE_CHECKOUT_URL,
} from '../config';

// Fetch al server de pagos de Atlas con el auth de Atlas SIEMPRE puesto.
// Usar SIEMPRE esto para pegarle al payment server (en vez de `fetch` pelado) →
// garantiza que todo request lleve el `Authorization: <GENERATE_AUTH_TOKEN>` que
// el server valida, y el `Content-Type: application/json` por defecto.
//
// `pathOrUrl` puede ser un path relativo ('/rebill/change-plan') o una URL
// completa (se usa tal cual). Headers extra pisan los defaults si hace falta.
export function paymentServerFetch(
  pathOrUrl: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = /^https?:\/\//.test(pathOrUrl)
    ? pathOrUrl
    : `${PAYMENT_SERVER_URL}${pathOrUrl}`;
  return fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
      ...(init.headers || {}),
    },
  });
}

// ─── Stripe: checkout + billing portal ──────────────────────────────────────
// Antes eran webhooks de n8n (`stripe-start` / `billing-portal`), que ante
// cualquier problema devolvían un 4xx/5xx pelado. El payment server devuelve
// errores TIPADOS: `{ ok:false, error, message }`. Ese `message` es dev-facing
// ("Usuario sin empresa: <uuid>", "Plan inválido: x") → NO se muestra nunca;
// lo que se muestra es la traducción del código `error`.
const STRIPE_ERROR_MESSAGES: Record<string, string> = {
  invalid_plan: 'El plan seleccionado no está disponible. Actualizá la página y probá de nuevo.',
  bad_request: 'No pudimos procesar el pedido. Actualizá la página y probá de nuevo.',
  user_not_found: 'No encontramos tu cuenta. Cerrá sesión, volvé a entrar y probá de nuevo.',
  company_not_found: 'No encontramos tu empresa. Escribinos y lo resolvemos.',
  // La empresa ya está activa en MercadoPago/Rebill: cobrarle también por Stripe
  // la dejaría con dos suscripciones. El cambio de plan va por el flujo de AR.
  platform_mismatch:
    'Tu suscripción se gestiona con otro medio de pago. Cambiá tu plan desde la sección "Plan".',
  // Billing portal sin haber pagado nunca por Stripe: no hay portal que abrir.
  no_stripe_customer:
    'Todavía no tenés pagos con tarjeta internacional, así que no hay portal de facturación para abrir.',
  stripe_not_configured:
    'Los pagos con tarjeta no están disponibles en este momento. Probá de nuevo en unos minutos.',
  unauthorized: 'No pudimos validar tu sesión. Cerrá sesión, volvé a entrar y probá de nuevo.',
};

/** Traduce la respuesta de error del payment server a un texto mostrable al usuario. */
async function stripeErrorMessage(resp: Response, fallback: string): Promise<string> {
  try {
    const data = await resp.json();
    const code = typeof data?.error === 'string' ? data.error : '';
    if (code && STRIPE_ERROR_MESSAGES[code]) return STRIPE_ERROR_MESSAGES[code];
  } catch {
    // Cuerpo vacío o no-JSON (502 de Render, timeout, etc.) → mensaje genérico.
  }
  return fallback;
}

/** POST /stripe/checkout → URL de la Checkout Session de Stripe (redirect). */
export async function fetchStripeCheckoutUrl(body: {
  user_id: string;
  plan: string;
  con_descuento?: boolean;
  cupon_id?: string;
}): Promise<string> {
  const fallback = 'No se pudo iniciar el pago. Probá de nuevo en unos minutos.';
  const resp = await paymentServerFetch(STRIPE_CHECKOUT_URL, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await stripeErrorMessage(resp, fallback));
  const data = await resp.json();
  if (!data?.url) throw new Error('No se recibió una URL válida para el checkout');
  return data.url as string;
}

/** POST /stripe/billing-portal → URL del portal de facturación de Stripe (redirect). */
export async function fetchStripeBillingPortalUrl(userId: string): Promise<string> {
  const fallback = 'No se pudo abrir el portal de facturación.';
  const resp = await paymentServerFetch(STRIPE_BILLING_PORTAL_URL, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
  if (!resp.ok) throw new Error(await stripeErrorMessage(resp, fallback));
  const data = await resp.json();
  if (!data?.url) throw new Error('URL inválida recibida');
  return data.url as string;
}
